import React from 'react';
import styled from 'styled-components';

import { CsvImportBucketGridColumn } from '@/features/csvImport/admin/types/csvImport';
import { Paginator } from '@/ui/Paginator';

const ROW_MIN_HEIGHT = 64;

/**
 * `@/ui/AutoTable` and `@/ui/Table` are styled as standalone tables — outer cell borders,
 * rounded corners and a filled header — all of which would double up on the border of the
 * section card this grid sits flush inside. Rather than fight those selectors from the
 * outside, the grid owns its markup and reuses `Paginator` for the paging control.
 */
const TableStyled = styled.table`
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  background: none;
`;

const HeaderCellStyled = styled.th`
  ${(p) => p.theme.typography.overline};
  height: 44px;
  padding: 0 20px;
  text-align: left;
  vertical-align: middle;
  border-bottom: 1px solid ${(p) => p.theme.colors.neutral.outline1};
  color: ${(p) => p.theme.colors.neutral.onBackground};
`;

const CellStyled = styled.td`
  ${(p) => p.theme.typography.caption};
  box-sizing: border-box;
  /* On a cell, height behaves as a minimum, so tall content still grows the row */
  height: ${ROW_MIN_HEIGHT}px;
  padding: 14px 20px;
  vertical-align: middle;
  overflow-wrap: anywhere;
  border-bottom: 1px solid ${(p) => p.theme.colors.neutral.outline1};
  color: ${(p) => p.theme.colors.neutral.onBackground};
`;

const FooterStyled = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background-color: ${(p) => p.theme.colors.neutral.container};
`;

/**
 * Grouped so a grid cannot be handed paging controls without a handler to act on them:
 * `@/ui/Paginator` treats its `onPageChange` as optional and would render enabled buttons
 * whose clicks go nowhere.
 */
type CsvImportBucketGridPagination = {
  /** Zero-based index of the page `rows` holds. */
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
};

type CsvImportBucketGridProps<Row> = {
  columns: CsvImportBucketGridColumn<Row>[];
  rows: Row[];
  rowKey: (row: Row) => string;
  /** Omit for a grid that shows every row it is given. */
  pagination?: CsvImportBucketGridPagination;
  className?: string;
};

export const CsvImportBucketGrid = <Row,>({
  columns,
  rows,
  rowKey,
  pagination,
  className,
}: CsvImportBucketGridProps<Row>): JSX.Element => {
  const showPaginator = !!pagination && pagination.pageCount > 1;

  return (
    <div className={className}>
      <TableStyled>
        <colgroup>
          {columns.map((column, index) => (
            <col key={index} width={column.width} />
          ))}
        </colgroup>
        <thead>
          <tr>
            {columns.map((column, index) => (
              <HeaderCellStyled key={index} scope="col">
                {column.header}
              </HeaderCellStyled>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={rowKey(row)}>
              {columns.map((column, index) => (
                <CellStyled key={index}>{column.render(row)}</CellStyled>
              ))}
            </tr>
          ))}
        </tbody>
      </TableStyled>
      {showPaginator && (
        <FooterStyled>
          <Paginator page={pagination.page} pageCount={pagination.pageCount} onPageChange={pagination.onPageChange} />
        </FooterStyled>
      )}
    </div>
  );
};
